import React, { useEffect, useMemo, useState } from "react"
import lodash from "lodash"
import BackOfficeAPIClient from "../../clients/BackofficeAPIClient"
import { Site } from "../../types/Site"
import { SiteOption } from "../../types/SiteOption"
import { useBrands } from "../../hooks/useBrands"
import ColorPicker from "./ColorPicker"
import OfferPreview from "./OfferPreview"
import { computeTextColor, DEFAULT_PDF_COLOR, isValidHexColor } from "./colorUtils"

interface PdfSetting {
    dealershipId: number | null
    brand: string | null
    primaryColor: string
    textColor: string
}

interface ColorsState {
    globalColor: string
    colorBySite: Record<number, string>
    colorByBrand: Record<string, string>
}

type Tab = 'global' | 'sites' | 'brands'

const backofficeApiClient = new BackOfficeAPIClient()

function countChanges(current: Record<string, string>, saved: Record<string, string>): number {
    const keys = new Set([ ...Object.keys(current), ...Object.keys(saved) ])
    return Array.from(keys).filter(key => current[key]?.toUpperCase() !== saved[key]?.toUpperCase()).length
}

export default function List() {
    const [ globalColor, setGlobalColor ] = useState<string>(DEFAULT_PDF_COLOR)
    const [ colorBySite, setColorBySite ] = useState<Record<number, string>>({})
    const [ colorByBrand, setColorByBrand ] = useState<Record<string, string>>({})
    const [ sites, setSites ] = useState<Site[]>([])
    const [ previewSite, setPreviewSite ] = useState<Site>()
    const [ activeTab, setActiveTab ] = useState<Tab>('global')
    const [ globalPreviewColor, setGlobalPreviewColor ] = useState<string>()
    const [ isSaving, setIsSaving ] = useState(false)
    const [ isLoadingSites, setIsLoadingSites ] = useState(true)
    const [ isLoadingSettings, setIsLoadingSettings ] = useState(true)
    const [ saveMessage, setSaveMessage ] = useState<string>()
    // Dernier état chargé / enregistré, référence pour compter les modifications
    const [ savedColors, setSavedColors ] = useState<ColorsState>({
        globalColor: DEFAULT_PDF_COLOR,
        colorBySite: {},
        colorByBrand: {},
    })

    const siteOptions = useMemo<SiteOption[]>(() => sites.map(site => ({
        value: site.IDSite,
        label: String(site.Descr),
        idPlaque: site.IDPlaque ?? site.IDSite,
    })), [ sites ])

    const { brands, loading: isLoadingBrands } = useBrands(siteOptions, true)

    const fetchSites = async () => {
        try {
            const json = await backofficeApiClient.get<Site[]>('/sites')
            setSites(lodash.sortBy(json, site => site.Plaque_Libelle))
        } catch (e) {
            console.error(e)
        } finally {
            setIsLoadingSites(false)
        }
    }

    const applyColors = (colors: ColorsState) => {
        setGlobalColor(colors.globalColor)
        setColorBySite(colors.colorBySite)
        setColorByBrand(colors.colorByBrand)
    }

    const fetchPdfSettings = async () => {
        try {
            const settings = await backofficeApiClient.get<PdfSetting[]>('/pdf-settings')
            const colorsBySite: Record<number, string> = {}
            const colorsByBrand: Record<string, string> = {}
            let fetchedGlobalColor = DEFAULT_PDF_COLOR

            settings.forEach((setting) => {
                if (setting.dealershipId === null && setting.brand === null) {
                    fetchedGlobalColor = setting.primaryColor.toUpperCase()
                } else if (setting.brand === null) {
                    colorsBySite[setting.dealershipId as number] = setting.primaryColor.toUpperCase()
                } else if (setting.dealershipId === null) {
                    colorsByBrand[setting.brand] = setting.primaryColor.toUpperCase()
                }
            })

            applyColors({ globalColor: fetchedGlobalColor, colorBySite: colorsBySite, colorByBrand: colorsByBrand })
            setSavedColors({ globalColor: fetchedGlobalColor, colorBySite: colorsBySite, colorByBrand: colorsByBrand })
        } catch (e) {
            console.error('Erreur lors du chargement des couleurs des documents :', e)
        } finally {
            setIsLoadingSettings(false)
        }
    }

    useEffect(() => {
        fetchSites()
        fetchPdfSettings()
    }, [])


    const changesByTab: Record<Tab, number> = {
        global: globalColor.toUpperCase() !== savedColors.globalColor.toUpperCase() ? 1 : 0,
        sites: countChanges(colorBySite, savedColors.colorBySite),
        brands: countChanges(colorByBrand, savedColors.colorByBrand),
    }
    const changesCount = changesByTab.global + changesByTab.sites + changesByTab.brands

    const tabs: { key: Tab, label: string, hasInvalidColor: boolean }[] = [
        { key: 'global', label: 'Global', hasInvalidColor: !isValidHexColor(globalColor) },
        { key: 'sites', label: 'Par site', hasInvalidColor: Object.values(colorBySite).some(color => !isValidHexColor(color)) },
        { key: 'brands', label: 'Par marque', hasInvalidColor: Object.values(colorByBrand).some(color => !isValidHexColor(color)) },
    ]

    useEffect(() => {
        if (changesCount > 0) {
            setSaveMessage(undefined)
        }
    }, [ changesCount ])

    useEffect(() => {
        if (!saveMessage) {
            return
        }
        const timeout = setTimeout(() => setSaveMessage(undefined), 3000)
        return () => clearTimeout(timeout)
    }, [ saveMessage ])

    // Les marques sont déduites des sites : leur chargement ne démarre qu'une fois les sites reçus
    const isLoadingByTab: Record<Tab, boolean> = {
        global: isLoadingSettings,
        sites: isLoadingSettings || isLoadingSites,
        brands: isLoadingSettings || isLoadingSites || isLoadingBrands,
    }

    const isBannerVisible = changesCount > 0 || isSaving || !!saveMessage

    const hasInvalidColor = tabs.some(tab => tab.hasInvalidColor)

    const handleSaveAll = async () => {
        setIsSaving(true)
        setSaveMessage(undefined)
        try {
            const configurations = [
                { dealershipId: null, brand: null, color: globalColor },
                ...Object.entries(colorBySite).map(([ siteId, color ]) => ({
                    dealershipId: parseInt(siteId),
                    brand: null,
                    color,
                })),
                ...Object.entries(colorByBrand).map(([ brand, color ]) => ({
                    dealershipId: null,
                    brand,
                    color,
                })),
            ].map(({ color, ...target }) => ({
                ...target,
                primaryColor: color,
                textColor: computeTextColor(color),
            }))

            await backofficeApiClient.post('/pdf-settings', { configurations })
            setSavedColors({ globalColor, colorBySite, colorByBrand })
            setSaveMessage('Couleurs enregistrées')
        } catch (e) {
            console.error('Erreur lors de la sauvegarde des couleurs des documents :', e)
            alert('Erreur lors de la sauvegarde')
        } finally {
            setIsSaving(false)
        }
    }

    // Couleur vide = retour à l'héritage : on supprime la clé
    const handleSiteColorChange = (siteId: number, color: string) => {
        setColorBySite(prev => color === '' ? lodash.omit(prev, siteId) : { ...prev, [siteId]: color })
    }

    const handleBrandColorChange = (brand: string, color: string) => {
        setColorByBrand(prev => color === '' ? lodash.omit(prev, brand) : { ...prev, [brand]: color })
    }

    const inheritedGlobalColor = isValidHexColor(globalColor) ? globalColor : DEFAULT_PDF_COLOR

    return (
        <div className="tw-flex tw-flex-col tw-p-4 tw-gap-5" style={{ paddingBottom: isBannerVisible ? 80 : undefined }}>
            <div className="tw-flex tw-flex-col tw-gap-1">
                <h1 className="tw-font-bold tw-text-3xl tw-mb-0">Couleur des documents (Offre / BDC)</h1>
                <p className="tw-text-gray-600 tw-mb-0">
                    Définissez la couleur des bandeaux, titres de section et éléments d'accentuation des offres et bons de
                    commande générés par le CRM, sans modifier leur structure ni leur contenu.
                </p>
            </div>
            <div
                className="tw-rounded tw-px-4 tw-py-3 tw-text-gray-600"
                style={{ backgroundColor: '#F8F9FA', border: '1px solid #E5E7EB', fontSize: 13 }}
            >
                <div>
                    <strong className="tw-text-gray-900">Règle de priorité à la génération</strong> (du plus spécifique au
                    plus générique) : couleur de la <strong className="tw-text-gray-900">marque</strong> du véhicule si
                    définie → sinon couleur du <strong className="tw-text-gray-900">site</strong> si définie → sinon couleur{' '}
                    <strong className="tw-text-gray-900">globale du groupe</strong> → sinon vert historique (valeur de
                    compatibilité). La couleur s'applique uniquement aux bandeaux, titres de section et éléments
                    d'accentuation — la structure, le contenu et la mise en page des documents restent inchangés.
                </div>
                <div>
                    <strong className="tw-text-gray-900">Contraste automatique :</strong> la couleur du texte affiché sur le
                    bandeau (noir ou blanc) est calculée automatiquement à partir de la couleur de fond choisie, aux trois
                    niveaux — aucune saisie supplémentaire n'est demandée à l'administrateur.
                </div>
            </div>

            <ul className="nav nav-tabs">
                {tabs.map(tab => (
                    <li key={tab.key} className="nav-item">
                        <a
                            href={`#${tab.key}`}
                            className={`nav-link${activeTab === tab.key ? ' active' : ''}`}
                            onClick={(e) => {
                                e.preventDefault()
                                setActiveTab(tab.key)
                            }}
                        >
                            {tab.label}
                            {tab.hasInvalidColor && <span className="badge badge-danger tw-ml-2">!</span>}
                            {!tab.hasInvalidColor && changesByTab[tab.key] > 0 && (
                                <span className="badge badge-primary tw-ml-2" title="Modifications non enregistrées">
                                    {changesByTab[tab.key]}
                                </span>
                            )}
                        </a>
                    </li>
                ))}
            </ul>

            {isLoadingByTab[activeTab] && (
                <div className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-gap-2 tw-py-12 tw-text-gray-500">
                    <i className='fa fa-spinner fa-spin fa-fw'></i>
                    Chargement de la configuration...
                </div>
            )}

            {activeTab === 'global' && !isLoadingByTab.global && <div className="tw-flex tw-flex-col tw-gap-3">
                <h2 className="tw-font-bold tw-text-xxl">Configuration globale</h2>
                <p className="tw-pl-5 tw-text-gray-600 tw-mb-0">
                    Couleur par défaut appliquée aux bandeaux, titres de section et éléments de mise en valeur des offres
                    et bons de commande, pour tous les sites et marques sans couleur spécifique.
                </p>
                <div className="tw-flex tw-flex-row tw-gap-8 tw-items-start tw-pl-5 tw-flex-wrap">
                    <div className="tw-flex tw-flex-row tw-gap-4 tw-items-center">
                        <span className="tw-whitespace-nowrap">Couleur du groupe :</span>
                        <ColorPicker
                            value={globalColor}
                            onChange={setGlobalColor}
                            onPreviewChange={setGlobalPreviewColor}
                            showPreview={false}
                        />
                    </div>
                    <div className="tw-flex-1 tw-flex tw-justify-center">
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <span className="tw-text-gray-500 tw-font-bold tw-uppercase" style={{ fontSize: 11 }}>
                                Aperçu{globalPreviewColor ? ' (non validé)' : ''}
                            </span>
                            <OfferPreview color={globalPreviewColor ?? inheritedGlobalColor} />
                        </div>
                    </div>
                </div>
            </div>}

            {activeTab === 'sites' && !isLoadingByTab.sites && <div className="tw-flex tw-flex-col tw-gap-3">
                <h2 className="tw-font-bold tw-text-xxl">Configuration par site</h2>
                <p className="tw-text-gray-600 tw-mb-0">
                    Surcharge la couleur du groupe pour un site donné. Pertinent pour les sites mono-marque, ou pour un
                    réglage uniforme d'un site quelle que soit la marque du document. Cliquez sur « Sélectionner une
                    couleur » (ou sur une pastille déjà définie) pour choisir une des 5 couleurs de base ou saisir un code
                    personnalisé.
                </p>
                <table className="table table-striped table-hover table-sm">
                    <thead>
                    <tr>
                        <th>Société</th>
                        <th>Site</th>
                        <th>Couleur</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {sites.map((site) => {
                        const siteColor = colorBySite[site.IDSite]
                        return (
                            <tr key={site.IDSite}>
                                <td className="tw-align-middle">{site.Plaque_Libelle}</td>
                                <td className="tw-align-middle">{site.Descr}</td>
                                <td>
                                    <ColorPicker
                                        value={siteColor ?? ''}
                                        onChange={(color) => handleSiteColorChange(site.IDSite, color)}
                                        inheritedColor={inheritedGlobalColor}
                                        inheritedFrom="groupe"
                                    />
                                </td>
                                <td className="tw-align-middle tw-text-right tw-whitespace-nowrap">
                                    {isValidHexColor(siteColor) && (
                                        <button type="button" className="btn btn-link btn-sm" onClick={() => setPreviewSite(site)}>
                                            Voir l'aperçu
                                        </button>
                                    )}
                                    {siteColor !== undefined && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => handleSiteColorChange(site.IDSite, '')}
                                        >
                                            Réinitialiser
                                        </button>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                    </tbody>
                </table>
            </div>}

            {activeTab === 'brands' && !isLoadingByTab.brands && <div className="tw-flex tw-flex-col tw-gap-3">
                <h2 className="tw-font-bold tw-text-xxl">Configuration par marque</h2>
                <p className="tw-text-gray-600 tw-mb-0">
                    Prioritaire sur la couleur du site : s'applique aux documents du véhicule de cette marque, sur tous
                    les sites qui la commercialisent. Sans couleur, la marque hérite de la couleur du site, puis du groupe.
                </p>
                <table className="table table-striped table-hover table-sm">
                    <thead>
                    <tr>
                        <th>Marque</th>
                        <th>Couleur</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {brands.map(({ value: brand }) => {
                        const brandColor = colorByBrand[brand]
                        return (
                            <tr key={brand}>
                                <td className="tw-align-middle">{brand}</td>
                                <td>
                                    <ColorPicker
                                        value={brandColor ?? ''}
                                        onChange={(color) => handleBrandColorChange(brand, color)}
                                        inheritedColor={inheritedGlobalColor}
                                        inheritedFrom="groupe, ou couleur du site si définie"
                                    />
                                </td>
                                <td className="tw-align-middle tw-text-right">
                                    {brandColor !== undefined && (
                                        <button
                                            type="button"
                                            className="btn btn-outline-secondary btn-sm"
                                            onClick={() => handleBrandColorChange(brand, '')}
                                        >
                                            Réinitialiser
                                        </button>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                    </tbody>
                </table>
            </div>}

            {previewSite && isValidHexColor(colorBySite[previewSite.IDSite]) && (
                <>
                    <div className="modal d-block" role="dialog" onClick={() => setPreviewSite(undefined)}>
                        <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Aperçu - {previewSite.Descr}</h5>
                                    <button type="button" className="close" aria-label="Fermer" onClick={() => setPreviewSite(undefined)}>
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body tw-flex tw-justify-center">
                                    <OfferPreview color={colorBySite[previewSite.IDSite]} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop show" />
                </>
            )}
            {isBannerVisible && <div
                className="tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-bg-white tw-flex tw-flex-row tw-justify-end tw-items-center tw-gap-3 tw-px-6 tw-py-3"
                style={{ borderTop: '1px solid #ddd', boxShadow: '0 -2px 6px rgba(0,0,0,.08)', zIndex: 1000 }}
            >
                {hasInvalidColor && <span className="tw-text-red-600">Une couleur saisie est invalide</span>}
                {!hasInvalidColor && changesCount > 0 && (
                    <span className="tw-font-bold">
                        {changesCount} modification{changesCount > 1 ? 's' : ''} non enregistrée{changesCount > 1 ? 's' : ''}
                    </span>
                )}
                {changesCount === 0 && saveMessage && <span className="tw-text-green-700">{saveMessage}</span>}
                <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => applyColors(savedColors)}
                    disabled={isSaving || changesCount === 0}
                >
                    Annuler
                </button>
                <button
                    type="button"
                    onClick={handleSaveAll}
                    className="btn btn-primary"
                    disabled={isSaving || hasInvalidColor || changesCount === 0}
                >
                    {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
            </div>}
        </div>
    )
}
